<template>
  <div class="w-full flex flex-col" v-if="!!tableSettings">
    <div class="m-2 p-2 bg-white rounded shadow flex flex-col" style="max-height: 100%; height: 100%">
      <div class="flex flex-row">
        <p class="p-2 text-lg font-medium text-primary flex-grow">
          {{ hideCancelled ? "" : "Cancelled " }} {{ currentRoute.name }}
        </p>
        <IconMenu :actions="[
          ...(routeMeta?.menuActions || []),
          ...analyticsAction,
          download,
          ...state.columnOnOffSwitchs,
          {
            break: true,
            label: hideCancelled ? 'Show Cancelled' : 'Hide Cancelled',
            action() {
              hideCancelled = !hideCancelled;
            },
          },
          {
            break: true,
            label: hideHidden ? 'Show Hidden' : 'Hide Hidden',
            action() {
              hideHidden = !hideHidden;
            },
          },
        ]" :contextData="contextData" />
      </div>
      <div class="px-2 pb-2" v-if="listFilterOptions.length">
        <div class="inline-flex border border-gray-200 rounded overflow-hidden" role="group" aria-label="List filter">
          <button v-for="option in listFilterOptions" :key="option.value" type="button"
            class="px-3 py-2 text-sm transition"
            :class="activeListFilter === option.value ? 'bg-primary text-white' : 'bg-white text-text hover:text-primary'"
            @click="activeListFilter = option.value">
            {{ option.label }}
          </button>
        </div>
      </div>
      <Loading message="Loading Items" v-if="state.loading" />
      <Input ref="quickSearch" label="Quick Search" v-model="quickFilter" />
      <DataTableV2 v-if="useEs" ref="table" :fetchRows="esFetchRows" @itemSelected="itemSelected"
      @optionsChanged="checkTableSettings" @dataError="onEsError" :paginationAutoPageSize="true"
        :quickFilter="quickFilter" :tableSettings="tableSettings" />
      <DataTable v-else ref="table" :items="items" @itemSelected="itemSelected" @optionsChanged="checkTableSettings"
        :paginationAutoPageSize="true" :quickFilter="quickFilter" :tableSettings="tableSettings" />
    </div>
  </div>
  <DynamicForm ref="form" @submitted="$forceUpdate" />
  <BulkDownloadModal ref="bulkDownloadModal" />
</template>
<script setup>
import {
  computed,
  onMounted,
  watch,
  ref,
  reactive,
  onBeforeMount,
  getCurrentInstance
} from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import { db } from "../../db.js";
import { 
  get, 
  set, 
  pickBy, 
  identity 
} from "lodash";
import DynamicForm from "../DynamicForm/DynamicForm.vue";
import BulkDownloadModal from "./BulkDownloadModal.vue";
import {
  DataTable,
  DataTableV2,
  IconMenu,
  Loading,
  Input,
} from "@comp/index.js";
import { useToast } from "vue-toastification";
import { getDefaultTableSettings } from "./defaultTableSettings.js";

const instance = getCurrentInstance();

// inistalise vue objects
const store = useStore();
const router = useRouter();
const route = useRoute();
const toast = useToast();

// set the component refs
let table = ref(null);
let form = ref(null);
let bulkDownloadModal = ref(null);

let routeMeta = ref(null);

// loading
const loading = ref(true);

// make them accessible in the template
const currentRoute = computed(() => route);

const quickFilter = ref("");

const hideCancelled = ref(true);
const hideHidden = ref(true);
const activeListFilter = ref(null);

const listFilterOptions = computed(() => routeMeta.value?.listFilters?.options || []);

// Set to true after the table has finished its initial settings restore so that
// checkTableSettings won't overwrite a saved filter with an empty model during
// the brief window between grid-init events and setTableSettings completing.
const tableReady = ref(false);

// Elasticsearch-backed (server-side) list mode. Enabled when the route sets
// `meta.esType`. Falls back to the RTDB store watcher if a search request fails.
const esFallback = ref(false);
const useEs = computed(() => !esFallback.value && !!routeMeta.value?.esType);

const columnIdentity = column => column.headerName || column.field || column.colId;

let state = reactive({
  loading: true,
  columnOnOffSwitchs: computed(() => {
    if (columnState.value) {
      return columnState.value.map((columnState, index) => {
        let color = columnState.hide ? "red" : "green";

        return {
          label: `<div class='w-3 h-3 p-0 m-2 rounded-full bg-${color}'></div><p class="text-${color}">${columnState.headerName}</p>`,
          break: index === 0,
          action() {
            table.value.columnApi.setColumnsVisible(
              [columnState.colId],
              columnState.hide
            );
          },
        };
      });
    }
    return [];
  }),
});

const tableSettings = computed(() => {
  const savedTableSettings = store.state.user?.Settings?.tables?.[routeMeta?.value?.tableSettings];
  
  // Priority: route.meta.defaultTableSettings > centralized defaults > null
  const defaultTableSettings = routeMeta.value?.defaultTableSettings 
    || getDefaultTableSettings(routeMeta?.value?.tableSettings);

  if (!savedTableSettings && !defaultTableSettings) {
    return null;
  }

  const _tableSettings =
  {
    ...(defaultTableSettings || {}),
    ...(savedTableSettings || {}),
    ...(routeMeta?.value?.tableSettingsOverride || {})
  };

  
  
  _tableSettings.columnState = [...new Map(
    (_tableSettings.columnState || []).map(column => [columnIdentity(column), column])
  ).values()];

  
  (routeMeta.value?.additionalTableHeaders || []).forEach((ath) => {
    let index = _tableSettings.columnState.findIndex((cs) => {
      return columnIdentity(cs) === columnIdentity(ath);
    });
    if (index > -1) {
      _tableSettings.columnState[index] = {
        ..._tableSettings.columnState[index],
        ...ath,
      };
    } else {
      _tableSettings.columnState.push(ath);
    }
  });
  
  if (routeMeta.value?.alterTableSettings) {
    routeMeta.value?.alterTableSettings(_tableSettings)
  }
  
  if (_tableSettings?.filter) {
    _tableSettings.filter = Object.fromEntries(
      Object.entries(_tableSettings.filter).map(([key, value]) => {
        return [key.replace("_", "."), value];
      })
    );
  }
  return _tableSettings;
});

const columnState = computed(() => {
  return tableSettings.value?.columnState || [];
});

const _items = computed(() => {
  const storedItems = store.state[routeMeta?.value?.dataPath];
  if (storedItems == null && routeMeta.value?.defaultEmptyData) return {};
  return storedItems;
});

// Get and compute all the items in the  view
// This is a computed property that returns an array of items.
const items = computed(() => {

  // Check if _items.value is null. If it is, return an empty array.
  if (!_items.value) {
    return [];
  } else {
    // If _items.value has data, filter and map it according to certain conditions.

    // Filter _items.value by DesignChangeFilter and routeMeta's filter function.
    let __items = Object.values(_items.value)
      .filter((i) => {
        if (routeMeta.value?.applyDesignChangeFilter !== false && store.state.user?.DesignChangeFilter) {
          let idList = Object.keys(store.state.user.DesignChangeFilter);
          return idList.includes(i.ID) || idList.includes(i.ParentID);
        } else return true;
      })
      .filter(routeMeta?.value?.filter || (() => true))
      .map(routeMeta?.value?.map || ((i) => i))
      .filter((item) => {
        const option = listFilterOptions.value.find(({ value }) => value === activeListFilter.value);
        return option?.filter ? option.filter(item) : true;
      })
      .filter((i) => {
        return !i.Cancelled === hideCancelled.value && !i.Hidden === hideHidden.value;
      })
      .map((item) => {

        // Create a copy of the item and add the ID key for consistency.
        let computedItem = {
          ...item,
          ID: item.ID,
        };

        // Depending on tableSettings' columnState or columns values, convert any Date field values to Date objects.
        if (tableSettings.value.columnState) {
          tableSettings.value.columnState.forEach((column) => {
            if (column.field.includes("Date.")) {
              function isDate(_date) {
                return (
                  _date &&
                  Object.prototype.toString.call(_date) === "[object Date]" &&
                  !isNaN(_date)
                );
              }
              let date = new Date(get(item, column.field));
              if (!isDate(date)) {
                set(computedItem, column.key, date);
              } else {
                set(computedItem, column.key, date);
              }
            }
          });
        } else if (tableSettings.value.columns) {
          tableSettings.value.columns.forEach((column) => {
            if (column.key.includes("Date.")) {
              set(computedItem, column.key, new Date(get(item, column.key)));
            }
          });
        }

        // Return the item with updated Date values.
        return computedItem;
      });

    // Return the filtered and mapped items.
    return __items;
  }
});

const itemSelected = (row) => {
  //if a command has been set run that command otherwise
  if (routeMeta?.value?.itemSelected) {
    routeMeta?.value?.itemSelected({
      ...contextData.value,
      item: row.data,
      instance: instance
    });
    return;
  }
  // then auto try to route to it
  let regex = RegExp("{(.*?)}");
  let match = routeMeta?.value?.selectionRoute.match(regex);
  router.push(
    `/${routeMeta?.value?.selectionRoute.replace(match[0], row.data[match[1]])}`
  );
};

let contextData = computed(() => {
  return {
    currentRoute: currentRoute.value,
    form: form.value,
    items: items.value,
    store,
    router,
    db,
    toast,
    instance: instance,
    meta: routeMeta.value?.formMetadata,
    loadingModal: bulkDownloadModal,
  };
});

const download = computed(() => {
  return {
    label: "Download CSV",
    action: table.value?.download,
    break: true,
  };
});

const analyticsAction = computed(() => {
  if (!routeMeta.value?.analyticsRoute) return [];
  return [{
    break: true,
    label: "View Analytics",
    action() {
      router.push(routeMeta.value.analyticsRoute);
    },
  }];
});

const checkTableSettings = async () => {
  if (routeMeta.value?.persistTableSettings === false) return;

  try {
    
    const columnApi = table.value.columnApi;
    const gridApi = table?.value?.tableApi;
    
    
    if (columnApi && gridApi) {
      const columns = columnApi.getColumns();
      const columnStates = columnApi.getColumnState();
      const columnDefs = gridApi.getColumnDefs();
      const _filter = gridApi.getFilterModel();

      // Guard: if the grid hasn't signalled readiness yet and the filter model
      // is empty while a non-empty filter is saved, this event was triggered by
      // initial table setup (before setTableSettings had a chance to restore the
      // filter). Skip the write to prevent overwriting the saved filter with {}.
      const savedFilter = tableSettings.value?.filter || {};
      if (!tableReady.value && Object.keys(_filter).length === 0 && Object.keys(savedFilter).length > 0) {
        return;
      }
      tableReady.value = true;

      const columnState = columnStates.map((columnState, index) => {
        const column = columns.find((columnDef) => {
          return columnDef.colDef.colId === columnState.colId;
        });

        return {
          ...columnState,
          type: columnDefs[index]?.type || "string",
          headerName: column?.colDef?.headerName,
          field: columnState.colId,
        };
      });
      const currentPage = table?.value?.tableApi?.paginationGetCurrentPage?.() ?? null;
      

      let ___tableSettings = pickBy(
        {
          columnState: columnState,
          filter: Object.fromEntries(
            Object.entries(_filter).map(([key, value]) => {
              return [key.replace(".", "_"), value];
            })
          )
        },
        identity
      );
      

      ___tableSettings.page = currentPage;

      // romove all keys with undefined values
      ___tableSettings.columnState = ___tableSettings.columnState.map((cs) => {
        return pickBy(cs, (value ) => value !== undefined);
      });
      
        
      await db.update(
        `Userdata/${store.getters.userID}/Settings/tables/${routeMeta?.value?.tableSettings}`,
        ___tableSettings
      );
    }
  } catch (e) {
    console.error(e);
  }
};

// TODO: Add a check to see if dataQuery is a function and call it if it is
function getListData(_meta) {
  
  if (typeof _meta?.value?.dataQuery === 'function') {
    _meta?.value?.dataQuery(store);
  } else if (_meta?.value?.dataQuery && _meta?.value?.dataQuery.key && _meta?.value?.dataQuery.value) {

    db.query(
      _meta?.value?.dataPath,
      _meta?.value?.dataQuery.key,
      _meta?.value?.dataQuery.value,
      store
    );
  } else {
    if (!_meta?.value?.dataPath) {
      return;
    }
    db.watch(_meta?.value?.dataPath, store);
  }
}

// Server-side row fetcher passed to DataTableV2. Translates the ag-grid request
// (page window + sort/filter models) into a db.searchDatabase call against the
// tenant's Elasticsearch index.
const esFetchRows = async ({ startRow, endRow, sortModel, filterModel }) => {
  const type = routeMeta.value?.esType;
  const sort = (sortModel || []).map((s) => ({ field: s.colId, dir: s.sort }));
  const res = await db.searchDatabase({
    type,
    query: quickFilter.value,
    from: startRow,
    size: endRow - startRow,
    sort,
    filters: filterModel,
    // The list toggles between active and cancelled/hidden items (binary).
    cancelled: !hideCancelled.value,
    hidden: !hideHidden.value,
  });
  let rows = res?.hits || [];
  const mapFn = routeMeta.value?.map;
  if (typeof mapFn === "function") {
    rows = rows.map(mapFn);
  }
  state.loading = false;
  return { rows, total: res?.total ?? rows.length };
};

// If Elasticsearch is unavailable, fall back to the original RTDB store watcher.
const onEsError = (err) => {
  esFallback.value = true;
  getListData(routeMeta);
};

onMounted(() => {

  if (!useEs.value) {
    getListData(routeMeta);
  }
});

onBeforeMount(async () => {
  
  if (currentRoute.value.meta.onBeforeMount) {
    await currentRoute.value.meta.onBeforeMount();
  }

  let meta = !!currentRoute.value.meta.lazyLoadMeta
    ? await currentRoute.value.meta.lazyLoadMeta()
    : {};

  routeMeta.value = {
    ...currentRoute.value.meta,
    ...meta,
  };
  activeListFilter.value = routeMeta.value?.listFilters?.default || null;

  loading.value = false;
});

watch(_items, (val) => {
  state.loading = useEs.value ? false : !val;
}, { immediate: true });

watch(() => routeMeta.value, (_meta) => {

  tableReady.value = false;
  loading.value = true;
  activeListFilter.value = _meta?.listFilters?.default || null;
  if (!useEs.value) {
    getListData(routeMeta);
  }
});

// In Elasticsearch mode, re-query when the active/cancelled or hidden toggles
// change (the grid holds cached pages that must be discarded).
watch([hideCancelled, hideHidden], () => {
  if (useEs.value && table.value?.purge) {
    table.value.purge();
  }
});
</script>
